import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import omit from 'lodash/omit';
import find from 'lodash/find';

import * as sharedTypes from 'src/actions/shared/sharedTypes';
import createCollectionResource from 'src/actions/shared/createResource';
import backToListView from 'src/actions/shared/backToListView';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { DomainError } from 'src/models/DomainError';
import { UpdateOptions } from 'src/actions/shared/createResource';
import { Webhook, WebhookOutgoing } from 'src/actions/integrations/webhook';
import { WebhookScheme } from './validation/schemas';
import { addWebhookSecrets } from 'src/actions/shared/validationHelpers';
import { logout } from 'src/actions/auth/authSlice';
import { toastSuccess, toastError } from 'src/actions/toast';

/**
 * These are the "read only" attributes that we receive from the backend and should remove
 * before posting the response back
 */
export const TRANSIENT_ATTRIBUTES = ['hasCredentials', 'headersKeys', 'createdAt', 'updatedAt'];

const webhooksResource = createCollectionResource<Webhook>({
  name: 'webhooks',
  endpoint: {
    addUrl: '/api/v1/webhooks',
    broadcastDeleteTopic: 'v1/orgs/:orgId/webhooks/:id/event/delete',
    broadcastTopic: 'v1/orgs/:orgId/webhooks/:id',
    collectionBroadcastDeleteTopic: 'v1/orgs/:orgId/webhooks/+/event/delete',
    collectionBroadcastTopic: 'v1/orgs/:orgId/webhooks/+',
    deleteUrl: '/api/v1/webhooks/:id',
    fetchAllUrl: '/api/v1/webhooks',
    updateUrl: '/api/v1/webhooks/:id',
  },
  options: {
    patchOnUpdate: true,
    toastMessages: {
      onAdd: () => 'Webhook created',
      onRemove: () => 'Webhook deleted',
      onUpdate: () => 'Webhook changed',
    },
  },
  observerCreator: ({ actions }) => {
    return ({ dispatch }) => {
      return {
        onBroadcast(item) {
          dispatch(actions.broadcast(item));
        },
        onBroadcastDelete(_: any, params) {
          dispatch(actions.broadcastDelete(params.id));
        },
        onUpdateSuccess(item) {
          if (item) {
            dispatch(actions.broadcast(item));
          }
        },
        onAddSuccess(item) {
          if (item) {
            dispatch(actions.broadcast(item));
          }
        },
      };
    };
  },
});

const removeTransientAttrs = <T extends object>(source: T): Partial<T> => {
  return omit(cloneDeep(source), TRANSIENT_ATTRIBUTES);
};

webhooksResource.effects.validate =
  <T extends WebhookOutgoing>(item: T, knownErrors?: DomainError[]) =>
  dispatch => {
    const withSecrets = removeTransientAttrs(addWebhookSecrets<any>(item));
    return dispatch(
      webhooksResource.effects.validateWithSchema(withSecrets, WebhookScheme, knownErrors, {
        toastMessage: 'Unable to save webhook. Fix the errors and try again.',
      })
    );
  };

const update: sharedTypes.AppThunk =
  (params: any, attrs: any, opts?: UpdateOptions) =>
  async (dispatch, getState, { gateways }) => {
    try {
      dispatch(updateStarted({}));
      await gateways.webhooks.update(params, attrs);
      dispatch(updateSuccess({}));

      if (!opts || !opts.disableWindowBack) {
        backToListView();
      }

      const toastMsg = get(opts, 'customToastMsg', 'Webhook changed');

      dispatch(toastSuccess(toastMsg));
    } catch (err) {
      if (err instanceof StandardDomainError) {
        const clonedErrors = cloneDeep(err.errors);

        const operationFailedError = find(clonedErrors, err => err.code === 'operation_failed');

        let toastMessage = clonedErrors[0].detail;

        if (operationFailedError) {
          toastMessage =
            'Failed to enable webhook, unable to ping URL. Verify that the server is operational and can accept events from Gladly.';
          operationFailedError.detail =
            'Unable to ping URL. Verify that the server is operational and can accept events from Gladly.';
        }

        dispatch(updateError(clonedErrors));
        dispatch(toastError(toastMessage));
        return;
      } else if (err instanceof Unauthorized) {
        dispatch(logout());
      } else {
        dispatch(updateRequestError(err));
      }
      dispatch(toastError('There was an error while updating'));
    }
  };

const { updateError, updateRequestError, updateStarted, updateSuccess } = webhooksResource.actions;
webhooksResource.effects.update = update;

export default webhooksResource;
